import React, { useMemo } from 'react';
import { HiPlus, HiX } from 'react-icons/hi';
import type { Descriptor } from '../../api/descriptors';
import type { RoleTagSuggestion } from '../../api/staff';
import { SearchableSelect } from '../common/SearchableSelect';

/** Staff Role code used when no standard role fits a title */
export const OTHER_ROLE_CODE = 'Other';

interface RoleTagPickerProps {
  /** Every Staff Role option, in the order the dropdown should list them */
  roleDescriptors: Descriptor[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  /** Roles the position title suggests; shown as one-click chips */
  suggestions?: RoleTagSuggestion[];
  loadingSuggestions?: boolean;
  /** Roles already on the position row itself (e.g. board roles); shown but not removable */
  lockedIds?: number[];
  /** Highlight the tag area, e.g. when a required tag is missing */
  invalid?: boolean;
  disabled?: boolean;
}

/**
 * Pick the standard roles (role tags) for a position.
 *
 * Tags are what the Role filter in Search Contacts matches, so they're shown
 * right under the position title rather than as a separate kind of
 * assignment.
 */
export const RoleTagPicker: React.FC<RoleTagPickerProps> = ({
  roleDescriptors,
  selectedIds,
  onChange,
  suggestions = [],
  loadingSuggestions = false,
  lockedIds = [],
  invalid = false,
  disabled = false
}) => {
  const labels = useMemo(() => {
    const map = new Map<number, string>();
    for (const d of roleDescriptors) map.set(d.descriptorId, d.shortDescription || d.codeValue);
    for (const s of suggestions) if (!map.has(s.descriptorId)) map.set(s.descriptorId, s.shortDescription);
    return map;
  }, [roleDescriptors, suggestions]);

  const label = (id: number) => labels.get(id) || `Role ${id}`;
  const otherDescriptor = roleDescriptors.find(d => d.codeValue === OTHER_ROLE_CODE);
  const shownIds = [...lockedIds, ...selectedIds.filter(id => !lockedIds.includes(id))];
  const pendingSuggestions = suggestions.filter(s => !shownIds.includes(s.descriptorId));
  const dropdownOptions = roleDescriptors.filter(
    d => !shownIds.includes(d.descriptorId) && d.codeValue !== OTHER_ROLE_CODE
  );

  const add = (id: number) => onChange([...selectedIds, id]);
  const remove = (id: number) => onChange(selectedIds.filter(x => x !== id));

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-white">Role tags</span>
        {loadingSuggestions && (
          <span className="text-xs text-gray-400">Checking title…</span>
        )}
      </div>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        Standard roles that let people find this contact with the Role filter in Search Contacts, whatever the local title.
      </p>

      <div
        className={`mt-2 flex min-h-[2.25rem] flex-wrap items-center gap-2 rounded-lg border p-2 ${
          invalid
            ? 'border-red-400 bg-red-50 dark:border-red-500 dark:bg-red-900/20'
            : 'border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-700'
        }`}
      >
        {shownIds.length === 0 && (
          <span className="text-sm italic text-gray-400">No tags yet</span>
        )}
        {shownIds.map(id => (
          <span
            key={id}
            className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
          >
            {label(id)}
            {!disabled && !lockedIds.includes(id) && (
              <button
                type="button"
                onClick={() => remove(id)}
                className="text-indigo-500 hover:text-indigo-700 dark:text-indigo-300"
                aria-label={`Remove ${label(id)}`}
              >
                <HiX className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>

      {!disabled && pendingSuggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Suggested from title:</span>
          {pendingSuggestions.map(s => (
            <button
              key={s.descriptorId}
              type="button"
              onClick={() => add(s.descriptorId)}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-indigo-300 px-2.5 py-0.5 text-xs text-indigo-700 hover:bg-indigo-50 dark:border-indigo-500 dark:text-indigo-300 dark:hover:bg-indigo-900/40"
            >
              <HiPlus className="h-3 w-3" />
              {s.shortDescription}
            </button>
          ))}
        </div>
      )}

      {!disabled && (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <SearchableSelect
            ariaLabel="Add a role tag"
            className="w-64 max-w-full"
            options={dropdownOptions.map(d => ({ value: d.descriptorId, label: d.shortDescription || d.codeValue }))}
            value={null}
            onChange={(id) => { if (id) add(id); }}
            placeholder="Add a role tag…"
          />
          {otherDescriptor && !shownIds.includes(otherDescriptor.descriptorId) && (
            <button
              type="button"
              onClick={() => add(otherDescriptor.descriptorId)}
              className="text-xs text-gray-600 underline hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
            >
              None of these fit
            </button>
          )}
        </div>
      )}
    </div>
  );
};
